import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { usePortalAuth } from '@/contexts/PortalAuthContext';
import { Loader2, Briefcase, FileText, Clock, ArrowRight } from 'lucide-react';
import { portalGet } from '@/lib/portal-api';

interface PortalStats {
  activeJobs: number;
  pendingSubmissions: number;
  totalSubmissions: number;
}

async function fetchPortalStats(): Promise<PortalStats> {
  return portalGet<PortalStats>('/stats');
}

interface RecentSubmission {
  id: string;
  candidateName: string;
  jobTitle: string;
  status: string;
  submittedAt: string;
}

async function fetchRecentSubmissions(): Promise<RecentSubmission[]> {
  return portalGet<RecentSubmission[]>('/submissions?limit=5&sort=recent');
}

function StatCard({
  label,
  value,
  icon: Icon,
  linkTo,
  color,
}: {
  label: string;
  value: number | undefined;
  icon: typeof Briefcase;
  linkTo: string;
  color: string;
}) {
  return (
    <Link
      to={linkTo}
      className="bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen p-6 hover:border-white/20 transition-colors group"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-text-secondary">{label}</p>
          <p className="text-3xl font-bold text-text-primary mt-2">
            {value !== undefined ? value : <Loader2 className="h-6 w-6 animate-spin text-text-tertiary" />}
          </p>
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <div className="flex items-center gap-1 mt-4 text-sm text-text-tertiary group-hover:text-emerald-400 transition-colors">
        <span>View details</span>
        <ArrowRight className="h-3.5 w-3.5" />
      </div>
    </Link>
  );
}

function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    submitted: 'Submitted',
    under_review: 'Under Review',
    shortlisted: 'Shortlisted',
    interview: 'Interview',
    offered: 'Offered',
    hired: 'Hired',
    rejected: 'Rejected',
    withdrawn: 'Withdrawn',
  };
  return labels[status] || status;
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'submitted':
      return 'text-blue-400 bg-blue-500/10';
    case 'under_review':
      return 'text-yellow-400 bg-yellow-500/10';
    case 'shortlisted':
      return 'text-emerald-400 bg-emerald-500/10';
    case 'interview':
      return 'text-purple-400 bg-purple-500/10';
    case 'offered':
      return 'text-cyan-400 bg-cyan-500/10';
    case 'hired':
      return 'text-green-400 bg-green-500/10';
    case 'rejected':
      return 'text-red-400 bg-red-500/10';
    case 'withdrawn':
      return 'text-gray-400 bg-gray-500/10';
    default:
      return 'text-text-secondary bg-white/5';
  }
}

function formatRelativeDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function PortalDashboardPage() {
  const { user } = usePortalAuth();

  const { data: stats, isLoading: isLoadingStats } = useQuery<PortalStats>({
    queryKey: ['portal', 'stats'],
    queryFn: fetchPortalStats,
    refetchInterval: 60000,
  });

  const { data: recentSubmissions, isLoading: isLoadingRecent } = useQuery<RecentSubmission[]>({
    queryKey: ['portal', 'submissions', 'recent'],
    queryFn: fetchRecentSubmissions,
    refetchInterval: 60000,
  });

  return (
    <div className="space-y-8">
      {/* Welcome Header */}
      <div>
        <h1 className="text-2xl font-bold text-text-primary">
          Welcome back, {user?.firstName}!
        </h1>
        <p className="text-text-secondary mt-1">
          Here is an overview of your recruitment activity with Opus Recruiter.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          label="Active Jobs"
          value={isLoadingStats ? undefined : stats?.activeJobs}
          icon={Briefcase}
          linkTo="/portal/jobs"
          color="bg-emerald-500/10 text-emerald-400"
        />
        <StatCard
          label="Pending Submissions"
          value={isLoadingStats ? undefined : stats?.pendingSubmissions}
          icon={Clock}
          linkTo="/portal/submissions"
          color="bg-yellow-500/10 text-yellow-400"
        />
        <StatCard
          label="Total Submissions"
          value={isLoadingStats ? undefined : stats?.totalSubmissions}
          icon={FileText}
          linkTo="/portal/submissions"
          color="bg-blue-500/10 text-blue-400"
        />
      </div>

      {/* Recent Submissions */}
      <div className="bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h2 className="text-lg font-semibold text-text-primary">Recent Submissions</h2>
          <Link
            to="/portal/submissions"
            className="text-sm text-emerald-400 hover:text-emerald-300 flex items-center gap-1 transition-colors"
          >
            View all
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        {isLoadingRecent ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-text-tertiary" />
          </div>
        ) : !recentSubmissions || recentSubmissions.length === 0 ? (
          <div className="py-12 text-center">
            <FileText className="h-10 w-10 text-text-tertiary mx-auto mb-3" />
            <p className="text-text-secondary">No submissions yet</p>
            <p className="text-sm text-text-tertiary mt-1">
              Candidate submissions will appear here once your recruiter shares them.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {recentSubmissions.map((submission) => (
              <div
                key={submission.id}
                className="flex items-center justify-between px-5 py-4 hover:bg-white/[0.02] transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-text-primary truncate">
                    {submission.candidateName}
                  </p>
                  <p className="text-xs text-text-tertiary mt-0.5 truncate">
                    {submission.jobTitle}
                  </p>
                </div>
                <div className="flex items-center gap-3 ml-4">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(submission.status)}`}
                  >
                    {getStatusLabel(submission.status)}
                  </span>
                  <span className="text-xs text-text-tertiary whitespace-nowrap">
                    {formatRelativeDate(submission.submittedAt)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
